import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "../../App";
import {
  FileText, Plus, Edit, Trash2, Copy, X, Save,
  Sparkles, Mail
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AiModelSelect, useAiModel } from "../../components/admin/AiModelSelect";

const Templates = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiModel, setAiModel] = useAiModel();
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    content: "",
    category: "general"
  });
  const [editingId, setEditingId] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const saveTemplate = async () => {
    try {
      if (editingId) {
        await axios.put(`${API}/templates/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: "Template updated" });
      } else {
        await axios.post(`${API}/templates`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: "Template created" });
      }
      fetchTemplates();
      closeModal();
    } catch {
      toast({ title: "Failed to save template", variant: "destructive" });
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Template deleted" });
      fetchTemplates();
    } catch {
      toast({ title: "Failed to delete", variant: "destructive" });
    }
  };

  const generateWithAI = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/generate`, {
        prompt_type: "email_draft",
        model: aiModel,
        context: {
          topic: formData.name || "Weekly specials",
          audience: formData.category === "general" ? "All leads" : formData.category
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData({ ...formData, content: response.data.content });
      toast({ title: "Content generated!" });
    } catch {
      toast({ title: "AI generation failed", variant: "destructive" });
    }
    setGenerating(false);
  };

  const openModal = (template = null) => {
    if (template) {
      setFormData({
        name: template.name,
        subject: template.subject,
        content: template.content,
        category: template.category
      });
      setEditingId(template.id);
    } else {
      setFormData({ name: "", subject: "", content: "", category: "general" });
      setEditingId(null);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({ name: "", subject: "", content: "", category: "general" });
    setEditingId(null);
  };

  const copyTemplate = (template) => {
    navigator.clipboard.writeText(template.content);
    toast({ title: "Content copied to clipboard" });
  };

  const getCategoryColor = (category) => {
    const colors = {
      general: "bg-slate-100 text-slate-700",
      neighborhood: "bg-blue-100 text-blue-700",
      specials: "bg-orange-100 text-orange-700",
      "off-market": "bg-purple-100 text-purple-700"
    };
    return colors[category] || colors.general;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0A2342] font-['Playfair_Display']" data-testid="templates-title">
            Email Templates
          </h1>
          <p className="text-slate-600">Create and manage reusable email templates</p>
        </div>
        <button
          onClick={() => openModal()}
          className="btn-primary flex items-center"
          data-testid="create-template-btn"
        >
          <Plus className="w-5 h-5 mr-2" /> New Template
        </button>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2342]"></div>
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 mb-4">No templates yet</p>
          <button
            onClick={() => openModal()}
            className="btn-primary"
          >
            Create Your First Template
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template, index) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-shadow"
              data-testid={`template-card-${template.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <span className={`badge ${getCategoryColor(template.category)}`}>
                  {template.category}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => copyTemplate(template)}
                    className="p-2 text-slate-400 hover:text-[#0A2342] transition-colors"
                    title="Copy content"
                    data-testid={`template-copy-${template.id}`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => openModal(template)}
                    className="p-2 text-slate-400 hover:text-[#0A2342] transition-colors"
                    data-testid={`template-edit-${template.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteTemplate(template.id)}
                    className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                    data-testid={`template-delete-${template.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <h3 className="font-semibold text-[#0A2342] mb-2">{template.name}</h3>
              <p className="text-sm text-slate-500 mb-3 flex items-center">
                <Mail className="w-4 h-4 mr-1" />
                {template.subject}
              </p>
              <p className="text-sm text-slate-600 line-clamp-3">{template.content}</p>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="template-modal"
          >
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#0A2342] font-['Playfair_Display']">
                {editingId ? "Edit Template" : "New Template"}
              </h2>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Template Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  placeholder="e.g., Weekly Specials Email"
                  data-testid="template-name-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="input-field"
                  data-testid="template-category-input"
                >
                  <option value="general">General</option>
                  <option value="neighborhood">Neighborhood Guide</option>
                  <option value="specials">Hidden Specials</option>
                  <option value="off-market">Off-Market</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Subject Line</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="input-field"
                  placeholder="e.g., What they aren't telling you about [Neighborhood]"
                  data-testid="template-subject-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-slate-700">Email Content</label>
                  <div className="flex items-center gap-3">
                  <AiModelSelect value={aiModel} onChange={setAiModel} />
                  <button
                    onClick={generateWithAI}
                    disabled={generating}
                    className="text-sm text-[#DE6E4B] hover:text-[#0A2342] flex items-center"
                    data-testid="template-ai-generate"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    {generating ? "Generating..." : "Generate with AI"}
                  </button>
                  </div>
                </div>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="textarea-field font-mono text-sm"
                  rows={12}
                  placeholder="Write your email content here... Use [Name] for personalization."
                  data-testid="template-content-input"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Use [Name] to personalize with recipient's name
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={saveTemplate}
                  className="btn-primary flex items-center"
                  data-testid="template-save-btn"
                >
                  <Save className="w-4 h-4 mr-2" /> Save Template
                </button>
                <button onClick={closeModal} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Templates;
